import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, PenLine, ShieldCheck, FileText, CheckCircle2, XCircle, Clock, AlertTriangle, Download,
  Smartphone, KeyRound, IdCard,
} from "lucide-react";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-2xl">
      <div className="flex items-center gap-2 mb-4 justify-center">
        <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><PenLine className="h-5 w-5" /></div>
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-500">Assietica Sign</p>
          <p className="font-heading font-semibold text-slate-800">Firma digitale del documento</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="sign-public-card">
        {children}
      </div>
      <p className="text-center text-xs text-slate-400 mt-4">© Assietica — Firma e accettazione digitale</p>
    </div>
  </div>
);

const StatusMsg = ({ icon: Icon, tone, title, text }) => (
  <div className="text-center py-6" data-testid="sign-public-status">
    <div className={`h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 ${tone}`}>
      <Icon className="h-7 w-7" />
    </div>
    <h2 className="font-heading text-xl font-semibold text-slate-800">{title}</h2>
    {text && <p className="text-slate-500 mt-2">{text}</p>}
  </div>
);

export default function SignPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [idUploaded, setIdUploaded] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  const fetchTx = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sign/public/${token}`);
      setData(data);
      if (data.signer_name && !fullName) setFullName(data.signer_name);
      setOtpSent(!!data.otp_sent);
      setPhoneMasked(data.phone_masked || "");
      setIdUploaded(!!data.id_document_uploaded);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Link non valido o scaduto");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchTx(); }, [fetchTx]);

  const requestOtp = async () => {
    setSendingOtp(true); setErr("");
    try {
      const { data } = await api.post(`/sign/public/${token}/request-otp`);
      setOtpSent(true);
      setPhoneMasked(data.phone_masked || "");
      if (!data.sent) setErr("Non è stato possibile inviare l'SMS. Contatta il tuo intermediario.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSendingOtp(false); }
  };

  const uploadId = async (file) => {
    if (!file) return;
    setUploadingId(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/sign/public/${token}/id-document`, fd);
      setIdUploaded(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploadingId(false); }
  };

  const sign = async () => {
    if (!fullName.trim()) { setErr("Indica nome e cognome"); return; }
    if (!consent) { setErr("Devi accettare per firmare"); return; }
    if (!otpCode.trim()) { setErr("Inserisci il codice OTP ricevuto via SMS"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/sign/public/${token}/sign`, { full_name: fullName, consent: true, otp_code: otpCode });
      setData(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/sign/public/${token}/reject`, { reason: "" });
      setData(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) {
    return <Shell><div className="py-10 flex items-center justify-center text-slate-400" data-testid="sign-public-loading"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div></Shell>;
  }

  if (err && !data) {
    return <Shell><StatusMsg icon={AlertTriangle} tone="bg-red-100 text-red-600" title="Link non disponibile" text={err} /></Shell>;
  }

  const status = data?.status;

  if (status === "FIRMATA") {
    const signedUrl = `${API}/sign/public/${token}/signed-document`;
    return (
      <Shell>
        <StatusMsg icon={CheckCircle2} tone="bg-emerald-100 text-emerald-600" title="Documento firmato" text={`Grazie. La firma è stata registrata${data.signed_at ? " il " + new Date(data.signed_at).toLocaleString("it-IT") : ""}. Il documento firmato è stato inviato via email a te e all'Agenzia.`} />
        <div className="text-center -mt-2">
          <a href={signedUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-700 font-medium" data-testid="sign-public-download-signed">
            <Download className="h-4 w-4" /> Scarica il documento firmato (con certificato)
          </a>
        </div>
      </Shell>
    );
  }
  if (status === "RIFIUTATA") {
    return <Shell><StatusMsg icon={XCircle} tone="bg-red-100 text-red-600" title="Firma rifiutata" text="Hai rifiutato la firma di questo documento." /></Shell>;
  }
  if (status === "SCADUTA") {
    return <Shell><StatusMsg icon={Clock} tone="bg-orange-100 text-orange-600" title="Link scaduto" text="Questo link di firma è scaduto. Contatta il tuo intermediario per riceverne uno nuovo." /></Shell>;
  }
  if (status === "ANNULLATA") {
    return <Shell><StatusMsg icon={XCircle} tone="bg-slate-100 text-slate-500" title="Pratica annullata" text="Questa pratica di firma è stata annullata." /></Shell>;
  }

  // IN_ATTESA → form di firma
  const docUrl = `${API}/sign/public/${token}/document`;
  const isStrong = data.verification_level === "OTP_ID";
  const canSign = fullName.trim() && consent && otpCode.trim() && (!isStrong || idUploaded);
  return (
    <Shell>
      <div className="space-y-5" data-testid="sign-public-form">
        <div>
          <p className="text-sm text-slate-500">Gentile <strong className="text-slate-800">{data.signer_name}</strong>, sei stato invitato a firmare il seguente documento.</p>
        </div>

        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0"><FileText className="h-6 w-6" /></div>
            <div className="min-w-0">
              <p className="font-medium text-slate-800 truncate">{data.document_title}</p>
              <p className="text-xs text-slate-400">Versione {data.document_version}</p>
            </div>
          </div>
          <a href={docUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-700" data-testid="sign-public-view-doc">
            <Download className="h-4 w-4" /> Visualizza / scarica il documento
          </a>
          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 rounded-md p-2">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Prova di integrità (SHA-256): <span className="font-mono break-all">{data.document_sha256}</span></span>
          </div>
        </div>

        <div>
          <label className="text-sm text-slate-600 mb-1 block">Nome e cognome del firmatario</label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Es. Mario Rossi" data-testid="sign-public-name" />
        </div>

        {/* Documento d'identità (solo livello OTP_ID) */}
        {isStrong && (
          <div className="rounded-xl border border-slate-200 p-4" data-testid="sign-public-id-section">
            <div className="flex items-center gap-2 mb-2">
              <IdCard className="h-4 w-4 text-sky-600" />
              <p className="text-sm font-medium text-slate-700">Documento d'identità</p>
            </div>
            {idUploaded ? (
              <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700" data-testid="sign-public-id-done"><CheckCircle2 className="h-4 w-4" /> Documento caricato correttamente</p>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-2">Carica una foto o scansione del tuo documento d'identità (carta d'identità, patente o passaporto). Formati: PDF, JPG, PNG.</p>
                <Input type="file" accept="application/pdf,image/*" disabled={uploadingId} onChange={(e) => uploadId(e.target.files?.[0])} data-testid="sign-public-id-file" />
                {uploadingId && <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Caricamento…</p>}
              </>
            )}
          </div>
        )}

        {/* Verifica OTP via SMS */}
        <div className="rounded-xl border border-slate-200 p-4" data-testid="sign-public-otp-section">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="h-4 w-4 text-sky-600" />
            <p className="text-sm font-medium text-slate-700">Verifica via SMS</p>
          </div>
          {!otpSent ? (
            <Button onClick={requestOtp} disabled={sendingOtp} variant="outline" className="w-full h-10" data-testid="sign-public-request-otp">
              {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />} Invia codice via SMS
            </Button>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2">Abbiamo inviato un codice a <strong>{phoneMasked || "il tuo numero"}</strong>. Inseriscilo qui sotto.</p>
              <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="Codice a 6 cifre" className="tracking-widest text-center font-mono" data-testid="sign-public-otp-input" />
              <button onClick={requestOtp} disabled={sendingOtp} className="text-xs text-sky-600 hover:text-sky-700 mt-2" data-testid="sign-public-resend-otp">Invia di nuovo il codice</button>
            </>
          )}
        </div>

        <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="sign-public-consent" />
          <span>Dichiaro di aver letto il documento e di accettarne il contenuto, apponendo la mia firma digitale.</span>
        </label>

        {err && <p className="text-sm text-red-600" data-testid="sign-public-error">{err}</p>}

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={sign} disabled={busy || !canSign} className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-public-sign-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PenLine className="h-4 w-4 mr-1.5" />} Firma il documento
          </Button>
          <Button onClick={reject} disabled={busy} variant="outline" className="h-11 text-slate-600" data-testid="sign-public-reject-btn">
            Rifiuta
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 text-center">Verranno registrati data, ora e dati tecnici della firma come evidenza.</p>
      </div>
    </Shell>
  );
}
